/** "X minutes not yet uploaded" (R8 on the recorder canvas): shown at the top
 * of every page whenever this computer holds recorded audio the server
 * hasn't got yet, plus "Recording in progress" while the live screen is on
 * another page. Built only from the device's own record -- minutes, start
 * time, expiry -- and never a client name (Prabash, 2026-10-17). */
import { RecorderEngine, RecordingEndedError } from "./recorder/engine.js";
import { httpRecorderApi } from "./recorder/api.js";
import { defaultStore } from "./recorder/store.js";
import type { EngineEvents, RecoverableRecording } from "./recorder/types.js";
import { currentCsrfToken } from "./api.js";
import { confirmDiscard } from "./recorder-live.js";
import { isSignedOut, SIGNED_OUT, unsentBanner } from "./recorder.js";
import { el } from "./render.js";

export interface UnsentBannerOptions {
  recordings: readonly RecoverableRecording[];
  uploading: ReadonlySet<string>;
  timeZone: string;
  now: number;
  liveRecording: boolean; // the live screen is running, on another page
  notice: string | null; // the last Upload now's outcome, when it didn't upload
  onBackToRecording: () => void;
  onUpload: (recordingId: string) => void;
  onDiscard: (recordingId: string, keepUploaded: boolean) => void;
}

function button(text: string, cls: string, onClick: () => void): HTMLButtonElement {
  const b = el("button", cls, text);
  b.type = "button";
  b.addEventListener("click", onClick);
  return b;
}

export function renderUnsentBanners(container: HTMLElement, opts: UnsentBannerOptions): void {
  const wrap = el("div", "pk-unsent-banners");
  if (opts.liveRecording) {
    const box = el("div", "pk-unsent pk-unsent-live");
    box.setAttribute("role", "status");
    box.append(el("span", undefined, "You're recording a meeting."), button("Back to recording", "btn btn-outline pk-btn-small", opts.onBackToRecording));
    wrap.append(box);
  }
  if (opts.notice) {
    const box = el("div", "pk-alert");
    box.setAttribute("role", "alert");
    box.textContent = opts.notice;
    wrap.append(box);
  }
  for (const r of opts.recordings) {
    const b = unsentBanner(r, opts.timeZone, opts.now);
    const box = el("div", b.level === "warn" ? "pk-unsent pk-unsent-warn" : "pk-unsent");
    box.setAttribute("role", b.level === "warn" ? "alert" : "status");
    const actions = el("div", "pk-unsent-actions");
    const busy = opts.uploading.has(r.recording_id);
    const upload = button(busy ? "Uploading…" : "Upload now", "btn btn-primary pk-btn-small", () => opts.onUpload(r.recording_id));
    upload.disabled = busy;
    actions.append(
      upload,
      button("Discard…", "btn btn-outline pk-btn-small", () =>
        confirmDiscard(r.unsent.minutes, (keep) => opts.onDiscard(r.recording_id, keep))
      )
    );
    box.append(el("span", "pk-unsent-text", b.text), actions);
    wrap.append(box);
  }
  if (wrap.childElementCount) container.append(wrap);
}

/** The device's waiting recordings (this host's only, #223), minus one the
 * live engine in this tab is still uploading. Never throws: a browser
 * without storage simply has none. */
export async function loadUnsent(excludeId: string | null): Promise<RecoverableRecording[]> {
  try {
    const api = httpRecorderApi(() => currentCsrfToken() ?? "");
    const all = await RecorderEngine.recoverable(defaultStore().store, api);
    return all.filter((r) => r.recording_id !== excludeId);
  } catch {
    return [];
  }
}

export const UPLOAD_FAILED = "Preekie couldn't upload that audio. Check you're online and signed in, then press Upload now again.";

/** Upload now: a fresh token and key from the server, then the waiting
 * audio. Resolves with a message to show when it didn't upload, or null.
 * If the server has ended the recording, the engine has already removed
 * the unreadable audio and says so (RecordingEndedError, #223). */
export async function uploadUnsent(recordingId: string): Promise<string | null> {
  const quiet = () => undefined;
  const events: EngineEvents = { onState: quiet, onLevels: quiet, onElapsed: quiet, onWarning: quiet, onUnsent: quiet, onError: quiet };
  let engine: RecorderEngine;
  try {
    engine = await RecorderEngine.recover(recordingId, events, { getCsrf: () => currentCsrfToken() ?? "" });
  } catch (err) {
    if (isSignedOut(err)) return SIGNED_OUT.message; // #303: the fresh token needs a signed-in session
    return err instanceof RecordingEndedError ? err.message : UPLOAD_FAILED;
  }
  try {
    const result = await engine.finished();
    if (result.status === "quarantined") return "The audio uploaded, but it didn't check out, so it's held for review. Nothing will be shared.";
    return result.status === "complete" ? null : UPLOAD_FAILED;
  } catch {
    return UPLOAD_FAILED;
  } finally {
    engine.shutdown();
  }
}

/** Discard from the banner: the server's copy of the key goes, then the device's audio. */
export async function discardUnsent(recordingId: string, keepUploaded: boolean): Promise<void> {
  const api = httpRecorderApi(() => currentCsrfToken() ?? "");
  await api.discard(recordingId, keepUploaded);
  await defaultStore().store.deleteRecording(recordingId);
}
